import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Shield, 
  CheckCircle,
  Globe,
  Heart,
  FileText,
  Users,
  Briefcase,
  BookOpen,
  Linkedin,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  MapPin,
  Award,
  Network,
  DollarSign,
  Building2,
  Newspaper,
  WifiOff,
  ClipboardCheck,
  Handshake
} from "lucide-react";
import logoImage from "@/assets/ptc-logo.png";

export function Footer({ showComplianceBadges = true }: { showComplianceBadges?: boolean }) {
  const { t } = useTranslation();
  return (
    <footer className="bg-foreground text-white py-6 md:py-12 mt-auto z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 lg:gap-8">
          {/* Column 1: Brand */}
          <div>
            <Link to="/" className="flex items-center space-x-3 mb-3 md:mb-4">
              <img 
                src={logoImage} 
                alt="Pharma Trial Connect" 
                className="w-auto h-10 md:h-20"
              />
            </Link>
            <p className="text-white/70 mb-1 text-xs md:text-sm font-semibold italic">
              Village to Verified: Africa's Fastest Clinical Pipeline
            </p>
            <p className="text-white/80 mb-2 md:mb-4 text-[10px] md:text-xs">
              AI-powered clinical trial matching & site readiness.
            </p>
            {showComplianceBadges && <div className="flex flex-wrap gap-1 md:gap-2">
              <Badge variant="outline" className="text-white border-white/20 text-[10px] md:text-xs">
                <Shield className="w-3 h-3 mr-1" />
                HIPAA
              </Badge>
              <Badge variant="outline" className="text-white border-white/20 text-[10px] md:text-xs">
                <Globe className="w-3 h-3 mr-1" />
                GDPR
              </Badge>
              <Badge variant="outline" className="text-white border-white/20 text-[10px] md:text-xs">
                <CheckCircle className="w-3 h-3 mr-1" />
                FDA
              </Badge>
              <Badge variant="outline" className="text-white border-white/20 text-[10px] md:text-xs">
                <Shield className="w-3 h-3 mr-1" />
                21 CFR Part 11
              </Badge>
            </div>}
          </div>
          
          {/* Column 2: Popular Conditions */}
          <div>
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Popular Conditions</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm">
              <li>
                <Link to="/conditions/cancer/breast-cancer" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Breast Cancer Trials
                </Link>
              </li>
              <li>
                <Link to="/conditions/cancer/lung-cancer" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Lung Cancer Trials
                </Link>
              </li>
              <li>
                <Link to="/conditions/diabetes/type2-diabetes" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Type 2 Diabetes
                </Link>
              </li>
              <li>
                <Link to="/conditions/heart/heart-failure" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Heart Failure Trials
                </Link>
              </li>
              <li>
                <Link to="/conditions/alzheimers" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Alzheimer's Trials
                </Link>
              </li>
              <li>
                <Link to="/conditions" className="hover:text-white flex items-center font-medium">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  View All Conditions →
                </Link>
              </li>
            </ul>
            
            <h3 className="font-semibold mb-2 md:mb-3 mt-4 text-sm md:text-base lg:text-lg">Company</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm">
              <li>
                <Link to="/about" className="hover:text-white flex items-center">
                  <Globe className="w-3 h-3 mr-2 hidden md:inline" />
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/mission" className="hover:text-white flex items-center">
                  <Globe className="w-3 h-3 mr-2 hidden md:inline" />
                  Mission & Vision
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-white flex items-center">
                  <Briefcase className="w-3 h-3 mr-2 hidden md:inline" />
                  Services
                </Link>
              </li>
              <li>
                <Link to="/careers" className="hover:text-white flex items-center">
                  <Briefcase className="w-3 h-3 mr-2 hidden md:inline" />
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/press" className="hover:text-white flex items-center">
                  <Newspaper className="w-3 h-3 mr-2 hidden md:inline" />
                  Press
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/diversity-inclusion" className="hover:text-white flex items-center">
                  <Users className="w-3 h-3 mr-2 hidden md:inline" />
                  Diversity & Inclusion
                </Link>
              </li>
              <li>
                <Link to="/partnerships" className="hover:text-white flex items-center">
                  <Handshake className="w-3 h-3 mr-2 hidden md:inline" />
                  Partnerships
                </Link>
              </li>
              <li>
                <Link to="/resources/partners/gulapro" className="hover:text-white flex items-center">
                  <Handshake className="w-3 h-3 mr-2 hidden md:inline" />
                  GulaPro — Recruitment Partner
                </Link>
              </li>
              <li>
                <Link to="/for-sponsors/feasibility" className="hover:text-white flex items-center">
                  <Handshake className="w-3 h-3 mr-2 hidden md:inline" />
                  Free 72-Hour Feasibility (Sponsors)
                </Link>
              </li>
              <li>
                <Link to="/for-sites/feasibility-ready" className="hover:text-white flex items-center">
                  <Handshake className="w-3 h-3 mr-2 hidden md:inline" />
                  Get Feasibility-Ready (PIs & Sites)
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Column 3: Global Regions */}
          <div>
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Global Regions</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm">
              <li>
                <Link to="/regions" className="hover:text-white flex items-center font-medium">
                  <Globe className="w-3 h-3 mr-2 hidden md:inline" />
                  All Regions →
                </Link>
              </li>
              <li>
                <Link to="/regions/united-states" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  United States
                </Link>
              </li>
              <li>
                <Link to="/regions/europe" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  Europe
                </Link>
              </li>
              <li>
                <Link to="/regions/africa" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  Africa
                </Link>
              </li>
              <li>
                <Link to="/regions/south-africa" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  South Africa
                </Link>
              </li>
              <li>
                <Link to="/regions/indonesia" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  Indonesia
                </Link>
              </li>
              <li>
                <Link to="/regions/india" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  India
                </Link>
              </li>
              <li>
                <Link to="/regions/japan" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  Japan
                </Link>
              </li>
              <li>
                <Link to="/regions/korea" className="hover:text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-2 hidden md:inline" />
                  South Korea
                </Link>
              </li>
            </ul>
            
            <h3 className="font-semibold mb-2 md:mb-3 mt-4 text-sm md:text-base lg:text-lg">Blog Categories</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm">
              <li>
                <Link to="/blog" className="hover:text-white flex items-center font-medium">
                  <Newspaper className="w-3 h-3 mr-2 hidden md:inline" />
                  All Articles →
                </Link>
              </li>
              <li>
                <Link to="/blog/category/regulations" className="hover:text-white">Regulations</Link>
              </li>
              <li>
                <Link to="/blog/category/safety" className="hover:text-white">Safety</Link>
              </li>
              <li>
                <Link to="/blog/category/research" className="hover:text-white">Research</Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Research Centers */}
          <div>
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Research Centers</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm">
              <li>
                <Link to="/directory/research-centers" className="hover:text-white flex items-center font-medium">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  All Centers →
                </Link>
              </li>
              <li>
                <Link to="/research-centers/south-africa" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  South Africa
                </Link>
              </li>
              <li>
                <Link to="/research-centers/kenya" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Kenya
                </Link>
              </li>
              <li>
                <Link to="/research-centers/nigeria" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Nigeria
                </Link>
              </li>
              <li>
                <Link to="/research-centers/ghana" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Ghana
                </Link>
              </li>
              <li>

                <Link to="/trial-sites/africa" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  African Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/trial-sites/kenya" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Kenya Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/trial-sites/nigeria" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Nigeria Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/trial-sites/ghana" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Ghana Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/trial-sites/south-africa" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  South Africa Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/trial-sites/rwanda" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Rwanda Trial Sites
                </Link>
              </li>
              <li className="pt-2 border-t border-white/10 mt-2">
                <Link to="/trials/hiv/africa" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  HIV Trials in Africa
                </Link>
              </li>
              <li>
                <Link to="/trials/tuberculosis/africa" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  TB Trials in Africa
                </Link>
              </li>
              <li>
                <Link to="/trials/malaria/africa" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Malaria Trials in Africa
                </Link>
              </li>
            </ul>

          </div>
          
          {/* Column 5: Resources + Company */}
          <div>
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Resources</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm mb-3 md:mb-4">
              <li>
                <Link to="/trials/paid" className="hover:text-white flex items-center font-medium">
                  <DollarSign className="w-3 h-3 mr-2 hidden md:inline" />
                  Paid Clinical Trials
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white flex items-center">
                  <BookOpen className="w-3 h-3 mr-2 hidden md:inline" />
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/news" className="hover:text-white flex items-center">
                  <Newspaper className="w-3 h-3 mr-2 hidden md:inline" />
                  News
                </Link>
              </li>
              <li>
                <Link to="/resources" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  Resources Hub
                </Link>
              </li>
              <li>
                <Link to="/resources/directory-for-sites" className="hover:text-white flex items-center">
                  <Building2 className="w-3 h-3 mr-2 hidden md:inline" />
                  Directory for Trial Sites
                </Link>
              </li>
              <li>
                <Link to="/resources/directory-for-sponsors" className="hover:text-white flex items-center">
                  <Users className="w-3 h-3 mr-2 hidden md:inline" />
                  Directory for Sponsors
                </Link>
              </li>
              <li>
                <Link to="/ptc-academy" className="hover:text-white flex items-center">
                  <Award className="w-3 h-3 mr-2 hidden md:inline" />
                  PTC Academy
                </Link>
              </li>
              <li>
                <Link to="/resources/fdora-calculator" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  FDORA Calculator
                </Link>
              </li>
              <li>
                <Link to="/ctms" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  CTMS Platform
                </Link>
              </li>
              <li>
                <Link to="/ctms/credentials" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  CTMS Credentials
                </Link>
              </li>
              <li>
                <Link to="/vs" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  CTMS Comparisons
                </Link>
              </li>
              <li>
                <Link to="/resources/ctms-roi-calculator" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  CTMS ROI Calculator
                </Link>
              </li>
              <li>
                <Link to="/resources/case-studies" className="hover:text-white flex items-center">
                  <Award className="w-3 h-3 mr-2 hidden md:inline" />
                  Success Stories
                </Link>
              </li>
              <li>
                <Link to="/education" className="hover:text-white flex items-center">
                  <BookOpen className="w-3 h-3 mr-2 hidden md:inline" />
                  Education Center
                </Link>
              </li>
              <li>
                <Link to="/guides" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/help" className="hover:text-white flex items-center">
                  <FileText className="w-3 h-3 mr-2 hidden md:inline" />
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/resources/validation-documentation" className="hover:text-white flex items-center">
                  <ClipboardCheck className="w-3 h-3 mr-2 hidden md:inline" />
                  System Validation
                </Link>
              </li>
              <li>
                <Link to="/trust" className="hover:text-white flex items-center">
                  <Shield className="w-3 h-3 mr-2 hidden md:inline" />
                  Trust & Evidence
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Column 6: Platform + Social Media */}
          <div>
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Platform</h3>
            <ul className="space-y-1 text-white/70 text-[10px] sm:text-xs md:text-sm mb-3 md:mb-4">
              <li>
                <Link to="/bridge" className="hover:text-white flex items-center">
                  <Network className="w-3 h-3 mr-2 text-[hsl(var(--chart-5))] hidden md:inline" />
                  BRIDGE™ (Live)
                </Link>
              </li>
              <li>
                <Link to="/inspection-readiness" className="hover:text-white flex items-center">
                  <ClipboardCheck className="w-3 h-3 mr-2 text-rose-500 hidden md:inline" />
                  Inspection Readiness
                </Link>
              </li>
              <li>
                <Link to="/offline-first" className="hover:text-white flex items-center">
                  <WifiOff className="w-3 h-3 mr-2 text-[hsl(var(--chart-4))] hidden md:inline" />
                  Offline-First
                </Link>
              </li>
              <li>
                <Link to="/trials" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Find Trials
                </Link>
              </li>
              <li>
                <Link to="/clinical-trial-finder" className="hover:text-white flex items-center">
                  <Heart className="w-3 h-3 mr-2 hidden md:inline" />
                  Trial Finder
                </Link>
              </li>
              <li>
                <Link to="/cro-services" className="hover:text-white flex items-center">
                  <Briefcase className="w-3 h-3 mr-2 hidden md:inline" />
                  CRO Services
                </Link>
              </li>
              <li>
                <Link to="/profile" className="hover:text-white flex items-center">
                  <Users className="w-3 h-3 mr-2 hidden md:inline" />
                  Profile
                </Link>
              </li>
            </ul>
            
            <h3 className="font-semibold mb-2 md:mb-3 text-sm md:text-base lg:text-lg">Follow Us</h3>
            <div className="flex flex-wrap gap-3">
              <a 
                href="https://www.linkedin.com/company/108588767" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit Pharma Trial Connect on LinkedIn"
                className="text-white/70 hover:text-white flex items-center transition-colors text-[10px] sm:text-xs md:text-sm"
              >
                <Linkedin className="w-3 h-3 md:w-4 md:h-4 mr-1" aria-hidden="true" />
                <span className="hidden md:inline">LinkedIn</span>
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61579220091763" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit Pharma Trial Connect on Facebook"
                className="text-white/70 hover:text-white flex items-center transition-colors text-[10px] sm:text-xs md:text-sm"
              >
                <Facebook className="w-3 h-3 md:w-4 md:h-4 mr-1" aria-hidden="true" />
                <span className="hidden md:inline">Facebook</span>
              </a>
              <a 
                href="https://x.com/pharma_trial" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit Pharma Trial Connect on Twitter/X"
                className="text-white/70 hover:text-white flex items-center transition-colors text-[10px] sm:text-xs md:text-sm"
              >
                <Twitter className="w-3 h-3 md:w-4 md:h-4 mr-1" aria-hidden="true" />
                <span className="hidden md:inline">Twitter/X</span>
              </a>
              <a 
                href="https://www.instagram.com/pharmatrialconnect/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit Pharma Trial Connect on Instagram"
                className="text-white/70 hover:text-white flex items-center transition-colors text-[10px] sm:text-xs md:text-sm"
              >
                <Instagram className="w-3 h-3 md:w-4 md:h-4 mr-1" aria-hidden="true" />
                <span className="hidden md:inline">Instagram</span>
              </a>
              <a 
                href="https://www.youtube.com/channel/UCvfKxXhQrm597l3BuaY96Sw" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit Pharma Trial Connect on YouTube"
                className="text-white/70 hover:text-white flex items-center transition-colors text-[10px] sm:text-xs md:text-sm"
              >
                <Youtube className="w-3 h-3 md:w-4 md:h-4 mr-1" aria-hidden="true" />
                <span className="hidden md:inline">YouTube</span>
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/20 mt-4 md:mt-6 lg:mt-8 pt-4 md:pt-6 lg:pt-8 text-center text-white/70">
          <p className="text-[10px] sm:text-xs md:text-sm">
            &copy; 2025 Pharma Trial Connect. All rights reserved. | 
            <a 
              href="https://pharmatrialconnect.com/sitemap.xml" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white transition-colors underline ml-2"
            >
              Sitemap
            </a>
            {" "} | {" "}
            <Link 
              to="/privacy-policy" 
              className="text-white/70 hover:text-white transition-colors underline"
            >
              Privacy Policy
            </Link>
            {" "} | {" "}
            <Link 
              to="/terms-of-service" 
              className="text-white/70 hover:text-white transition-colors underline"
            >
              Terms of Service
            </Link>
            {" "} | {" "}
            <Link 
              to="/accessibility" 
              className="text-white/70 hover:text-white transition-colors underline"
            >
              Accessibility
            </Link>
            {" "} | {" "}
            <Link 
              to="/gender-equality-plan" 
              className="text-white/70 hover:text-white transition-colors underline"
            >
              Gender Equality Plan
            </Link>
            {" "} | Managed by{" "}
            <a 
              href="https://konigdigital.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-white/80 transition-colors underline"
            >
              Konig Digital
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
